import axios, {AxiosResponse} from "axios";
import {
    call,
    CallEffect,
    put,
    PutEffect,
    putResolve,
    select,
    SelectEffect,
    take,
    TakeEffect,
    takeLatest,
} from "redux-saga/effects";
import {all, AllEffect, ForkEffect} from "@redux-saga/core/effects";
import {LocalizeState} from "react-localize-redux";
import {
    ContentAvailabilityAction,
    EditorType,
    ElementsValues,
    ElementsValuesMap,
    ElementsValuesType,
    getToken,
    GetTokenGeneratorType,
    history,
    HttpMethod,
    isResourceAvailable,
    S3MethodItem,
    safeSaga,
    SchemaElementsValues,
    setContentAvailability,
    setSchemaElementValue,
    SetSchemaElementValueAction,
    SignS3ResourceResponse,
    updateTaskElementsValues,
    UpdateTaskElementsValuesAction,
} from "@data-force/app";
import Nullable from "@data-force/app/interfaces/nullable";
import {Action} from "redux";
import {ContentHeadResponse} from "@data-force/app/interfaces/store/sagas/utils";
import {UploadFile} from "antd/lib/upload/interface";
import {message} from "antd";
import HistoryParams from "../../interfaces/historyParams";
import {
    getHistoryParams,
    getUpdatedElementMetadata,
    isJobExpired,
    isReadOnlyTask,
    mapPhaseStatus,
    mergeUploadBuckets,
} from "../../utils/common";
import {
    FetchTaskMetadataPayload,
    GetTaskResponse,
    Status,
    Task,
    TaskData,
    TaskDataType,
    TaskEntity,
    TaskListResponse,
} from "../../interfaces/store/actions/payload/task";
import {
    CHANGE_ACTIVE_TASK,
    GET_ACTIVE_TASK_DETAILS,
    GET_TASK_LIST,
    RESET_TASK_DATA,
    SAVE_ACTIVE_TASK,
    SAVE_COMPLETED,
    SELECT_TASK_IN_WIZARD,
    START_TASK_WIZARD,
    UPDATE_TASK_STATUS,
} from "../actions/availableActions";
import StoreState from "../../interfaces/store/storeState";
import {
    ChangeActiveTaskAction,
    GetActiveTaskDetailsAction,
    GetTaskListAction,
    SaveActiveTaskAction,
    SaveCompletedAction,
    SaveTaskSuccessAction,
    SelectTaskInWizardAction,
    SetActiveTaskByIdAction,
    SetTaskMaskDataAction,
    UpdateActiveTaskAction,
    UpdateActiveTaskPrivatesAction,
    UpdateTaskEntityByIdAction,
    UpdateTaskStatusAction,
} from "../../interfaces/store/actions/task";
import {
    getActiveTaskDetails,
    saveActiveTask,
    saveCompleted,
    saveTaskSuccess,
    setActiveTaskById,
    setTaskList,
    setTaskMaskData,
    updateActiveTask,
    updateActiveTaskPrivates,
    updateTaskEntityById,
    updateTaskStatus,
} from "../actions/task";
import {ContentGetResponse, TaskSaveResponse} from "../../interfaces/store/sagas/task";
import {
    getAnnotationUrl,
    getFluxUrl,
    getTaskMetadata,
    GetTaskMetadataGenerator,
    handleValidationError,
    metadataHasErrors,
    resolveEditorElement,
    retrieveElementsValues,
    RetrieveElementsValuesGenerator,
    saveAndReturnActiveTask,
    saveBatchFieldsData,
    signTaskFileHandler,
} from "./common";
import {clearAlerts, setFileSelectionType, showResetTaskDialog} from "../actions/layout";
import {
    ClearAlertsAction,
    SetFileSelectionTypeAction,
    ShowResetTaskDialogAction,
} from "../../interfaces/store/actions/layout";
import {removeSelectedFile, setFinishedUploads, signTaskFile} from "../actions/uploadFiles";
import {RemoveSelectedFileAction, SetFinishedUploadsAction} from "../../interfaces/store/actions/uploadFiles";
import {getJobPhase} from "../actions/job";
import {GetJobPhaseAction, SaveBatchFieldsAction} from "../../interfaces/store/actions/job";
import {BucketCount} from "../../interfaces/store/reducers/uploadFilesState";
import {FileSelectionType} from "../../interfaces/store/actions/payload/layout";
import {JobPhase} from "../../interfaces/store/actions/payload/job";

export type FetchAllTasksGenerator = Generator<SelectEffect
| Promise<AxiosResponse<TaskListResponse>>
| FetchAllTasksGenerator,
TaskEntity[],
string | AxiosResponse<TaskListResponse> | TaskEntity[]>;

export function* fetchAllTasks(currentCursor?: object): FetchAllTasksGenerator {
    const {projectId, jobId, phase}: HistoryParams = getHistoryParams();
    const fluxUrl: string = (yield getFluxUrl()) as string;

    const {data: {items, cursor}}: AxiosResponse<TaskListResponse> = (yield axios.get(
        `${fluxUrl}/project/${projectId}/job/${jobId}/task`, {
            params: {
                phaseName: phase,
                ...currentCursor,
            },
        },
    )) as AxiosResponse<TaskListResponse>;

    const other: TaskEntity[] = cursor ? (yield fetchAllTasks(cursor)) as TaskEntity[] : [];

    return [...(items || []), ...other];
}

type FetchTaskMetadataGenerator = Generator<SelectEffect
| PutEffect
| Promise<AxiosResponse<GetTaskResponse>>,
Nullable<ElementsValues>,
Action | AxiosResponse<GetTaskResponse> | string>;

function* fetchTaskMetadata(payload: FetchTaskMetadataPayload): FetchTaskMetadataGenerator {
    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const {taskId, token, schemaKey}: FetchTaskMetadataPayload = payload;

    const {data: {data: taskMetadata}}: AxiosResponse<GetTaskResponse> = (yield axios.get(
        `${annotationUrl}/task?token=${token}`,
    )) as AxiosResponse<GetTaskResponse>;

    if (taskMetadata) {
        yield put(updateTaskElementsValues({
            schemaKey,
            metadataId: taskId,
            values: taskMetadata,
        }));
    }

    return taskMetadata;
}

type FetchTasksElementsValuesGenerator = Generator<SelectEffect
| PutEffect
| AllEffect<GetTokenGeneratorType | FetchTaskMetadataGenerator>,
Nullable<ElementsValues>[],
Action | string | string[] | Nullable<ElementsValues>[]>;

function* fetchTasksElementsValues(operationalTasks: TaskEntity[]): FetchTasksElementsValuesGenerator {
    const {projectId, jobId, phase}: HistoryParams = getHistoryParams();
    const tokens: string[] = (yield all(
        operationalTasks.map(
            ({id}: TaskEntity): GetTokenGeneratorType => getToken({
                projectId,
                jobId,
                taskId: id,
                phase,
            }),
        ),
    )) as string[];

    const schemaKey: string = (
        yield select(({project: {projectDetails}}: StoreState): string => (
            `${projectDetails?.schemaId}_${phase}`
        ))
    ) as string;

    return (yield all(
        tokens.map((token: string, index: number): FetchTaskMetadataGenerator => fetchTaskMetadata({
            schemaKey,
            token,
            taskId: operationalTasks[index].id,
        })),
    )) as Nullable<ElementsValues>[];
}

type HandleFinishedUploadsGenerator = Generator<PutEffect<SetFinishedUploadsAction | SetFileSelectionTypeAction>
| SelectEffect
| CallEffect
| AllEffect<RetrieveElementsValuesGenerator>,
void,
string | ElementsValues[] | EditorType>;

function* handleFinishedUploads(tasks: TaskEntity[]): HandleFinishedUploadsGenerator {
    const finishedTasks: TaskEntity[] = tasks.filter(
        (task: TaskEntity) => [Status.COMPLETED, Status.REJECTED].includes(task.status),
    );
    if (!finishedTasks.length) {
        yield put(setFileSelectionType(FileSelectionType.BULK));
    }
    const {phase}: HistoryParams = getHistoryParams();
    const {uploadReference}: EditorType = (
        yield select(({schema: {parsedSchemas}, project: {projectDetails}}: StoreState): EditorType => (
            parsedSchemas[`${projectDetails!.schemaId}_${phase}`] as EditorType)!)
    ) as EditorType;

    if (finishedTasks && uploadReference) {
        yield call(fetchTasksElementsValues, finishedTasks);

        const tasksMetadata: ElementsValues[] = (yield all(
            finishedTasks.map((taskEntity: TaskEntity): RetrieveElementsValuesGenerator => retrieveElementsValues({
                metadataId: taskEntity.id,
                elementsValuesType: ElementsValuesType.TASK,
            })),
        )) as ElementsValues[];

        const bucketCount: BucketCount = {};
        tasksMetadata.forEach((elementsValues: ElementsValues) => {
            if (elementsValues[uploadReference]) {
                const upload: string = elementsValues[uploadReference] as string;
                if (!bucketCount[upload]) {
                    bucketCount[upload] = 0;
                }
                bucketCount[upload] += 1;
            }
        });

        const finishedTaskIds: string[] = finishedTasks.map((task: TaskEntity) => task.id);

        yield put(setFinishedUploads({bucketCount, finishedTaskIds}));
    }
}

type FetchTaskListGenerator = Generator<SelectEffect
| CallEffect
| PutEffect
| Promise<AxiosResponse<TaskListResponse>>
| FetchAllTasksGenerator
| FetchTasksElementsValuesGenerator,
void,
string | AxiosResponse<TaskListResponse> | TaskEntity[] | Action | Nullable<ElementsValues>[]>;

function* fetchTaskList(): FetchTaskListGenerator {
    const historyParams: HistoryParams = getHistoryParams();
    const {taskId}: HistoryParams = historyParams;
    const tasks: TaskEntity[] = [];
    const items: TaskEntity[] = (yield call(fetchAllTasks)) as TaskEntity[];

    if (items) {
        tasks.push(...mapPhaseStatus(items));

        const operationalTasks: TaskEntity[] = tasks.filter(
            (task: TaskEntity): boolean => [Status.REOPENED, Status.IN_PROGRESS].includes(task.status),
        );
        if (operationalTasks.length) {
            yield call(fetchTasksElementsValues, operationalTasks);
        }

        yield call(handleFinishedUploads, tasks);

        yield put(setTaskList(tasks));
        let activeTaskId: Nullable<string> = taskId;
        if (!activeTaskId) {
            const activeTask: Nullable<TaskEntity> = tasks.find(
                (task: TaskEntity): boolean => [
                    Status.READY,
                    Status.IN_PROGRESS,
                    Status.REOPENED,
                ].includes(task.status),
            );
            activeTaskId = activeTask?.payload || tasks[0].payload;
        }
        yield putResolve(setActiveTaskById(activeTaskId));
        if (activeTaskId) {
            yield put(getActiveTaskDetails());
        }
    }
}

type FetchActiveTaskDetails = Generator<SelectEffect
| PutEffect
| Promise<AxiosResponse<Task | ContentGetResponse | ContentHeadResponse> | boolean>
| PutEffect<UpdateActiveTaskPrivatesAction | ContentAvailabilityAction>
| CallEffect
| GetTokenGeneratorType
| FetchActiveTaskDetails,
void,
string
| AxiosResponse<Task
| ContentGetResponse>
| TaskEntity[]
| EditorType
| Task
| boolean
| UploadFile
| SignS3ResourceResponse>;

function* fetchActiveTaskData(): FetchActiveTaskDetails {
    const activeTask: Nullable<Task> = (
        yield select((state: StoreState): Nullable<Task> => state.job.activeTask)
    ) as Nullable<Task>;
    if (activeTask) {
        const {id: taskId}: Task = activeTask;
        const historyParams: HistoryParams = getHistoryParams();
        const {phase, projectId, jobId}: HistoryParams = historyParams;
        const taskList: TaskEntity[] = (
            yield select((state: StoreState): TaskEntity[] => state.job.tasks)
        ) as TaskEntity[];
        const taskEntity: Nullable<TaskEntity> = taskList.find(
            (currentTaskEntity: TaskEntity): boolean => (taskId === currentTaskEntity.payload),
        );
        const {id: taskEntityId}: TaskEntity = taskEntity!;
        const annotationUrl: string = (yield getAnnotationUrl()) as string;

        const taskToken: string = (yield getToken({
            projectId, jobId, taskId: taskEntityId!, phase,
        })) as string;

        const {data: {data: taskMetadata}}: AxiosResponse<GetTaskResponse> = (yield axios.get(
            `${annotationUrl}/task?token=${taskToken}`,
        )) as AxiosResponse<GetTaskResponse>;

        if (taskMetadata) {
            const schemaKey: string = (
                yield select(({project: {projectDetails}}: StoreState): string => (
                    `${projectDetails?.schemaId}_${phase}`
                ))
            ) as string;

            yield put(updateTaskElementsValues({
                schemaKey,
                metadataId: taskEntityId,
                values: taskMetadata,
            }));

            const {fileUrlReference, fileNameReference, uploadReference}: EditorType = (
                yield select(({schema: {parsedSchemas}}: StoreState): EditorType => (
                    parsedSchemas[schemaKey] as EditorType)!)
            ) as EditorType;

            if (fileUrlReference) {
                const fileReference: string = taskMetadata[fileUrlReference] as string;
                const fileName: Nullable<string> = fileNameReference ? taskMetadata[fileNameReference] as string : null;
                const upload: Nullable<string> = uploadReference ? taskMetadata[uploadReference] as string : null;

                if (fileReference) {
                    const signResponse: SignS3ResourceResponse = (yield call(signTaskFileHandler, signTaskFile({
                        historyParams,
                        taskEntity,
                        fieldItems: [{
                            fileField: fileUrlReference,
                            methods: [HttpMethod.GET, HttpMethod.HEAD],
                        }],
                    }))) as SignS3ResourceResponse;
                    const contentAvailableUrl: Nullable<string> = signResponse
                        .entityItems[0]?.fieldItems[0]?.methodItems.find(
                        (methodItem: S3MethodItem): boolean => methodItem.method === HttpMethod.HEAD
                    )?.token;
                    if (!contentAvailableUrl) {
                        throw Error("Invalid file sign!");
                    }
                    const contentAvailable: boolean = (yield call(isResourceAvailable, contentAvailableUrl)) as boolean;

                    if (contentAvailable) {
                        const contentUrl: Nullable<string> = signResponse
                            .entityItems[0]?.fieldItems[0]?.methodItems.find(
                            (methodItem: S3MethodItem): boolean => methodItem.method === HttpMethod.GET
                        )?.token;
                        if (!contentUrl) {
                            throw Error("Invalid file sign!");
                        }

                        yield put(updateActiveTaskPrivates({
                            signedMediaUrl: contentUrl,
                            fileName,
                            upload,
                        }));
                    } else {
                        yield put(setContentAvailability(false));
                    }
                } else {
                    yield put(updateActiveTaskPrivates({
                        upload: null,
                        signedMediaUrl: null,
                        fileName: null,
                    }));
                }
            }
        } else {
            yield put(updateActiveTaskPrivates({
                upload: null,
                signedMediaUrl: null,
                fileName: null,
            }));
        }

        yield put(setTaskMaskData({visible: false}));
    }
}

type ChangeActiveTaskGenerator = Generator<SelectEffect
| PutEffect<GetActiveTaskDetailsAction
| SaveBatchFieldsAction
| ClearAlertsAction
| GetTaskListAction
| SaveActiveTaskAction
| SetActiveTaskByIdAction
| SetTaskMaskDataAction>
| TakeEffect
| CallEffect,
void,
TaskEntity[] | Task | LocalizeState | boolean>;

function* changeActiveTaskHandler({payload: {movement}}: ChangeActiveTaskAction): ChangeActiveTaskGenerator {
    yield put(setTaskMaskData({visible: true}));
    message.destroy();
    yield putResolve(clearAlerts());
    const {
        idpId, phase, projectId, jobId, jobPhaseId, taskId,
    }: HistoryParams = getHistoryParams();

    const activeTask: Task = (yield call(saveAndReturnActiveTask, false)) as Task;
    const {id, valid: validTask}: Task = activeTask;

    const validJob: boolean = (
        yield call(metadataHasErrors, {metadataId: jobId, elementsValuesType: ElementsValuesType.JOB})
    ) as boolean;

    if ((validTask || movement === -1) && validJob) {
        const taskList: TaskEntity[] = (
            yield select((state: StoreState): TaskEntity[] => state.job.tasks)
        ) as TaskEntity[];

        yield call(saveBatchFieldsData, false);

        const taskIndex: Nullable<number> = taskList.findIndex(
            (taskEntity: TaskEntity): boolean => id === taskEntity.payload,
        );

        const indexOfTaskToOpen: number = taskIndex + movement;
        if (indexOfTaskToOpen >= 0 && indexOfTaskToOpen < taskList.length) {
            if (taskId) {
                history.push(`/${idpId}/${phase}/${projectId}/${jobId}/${jobPhaseId}`);
            }
            const taskToOpen: TaskEntity = taskList[taskIndex + movement];
            yield putResolve(setActiveTaskById(taskToOpen.payload));
            yield put(getActiveTaskDetails());
        }
    } else {
        yield put(setTaskMaskData({visible: false}));
        yield call(handleValidationError, id);
    }
}

type SaveActiveTaskGenerator = Generator<SelectEffect
| PutEffect<UpdateActiveTaskAction
| UpdateActiveTaskPrivatesAction
| SaveCompletedAction
| GetTaskListAction
| UpdateTaskEntityByIdAction
| SaveTaskSuccessAction
| GetJobPhaseAction
| UpdateTaskStatusAction
| UpdateTaskElementsValuesAction>
| CallEffect<boolean | ElementsValues | GetTaskMetadataGenerator | EditorType>
| Promise<AxiosResponse<TaskEntity>>
| GetTokenGeneratorType,
void,
boolean | string | AxiosResponse<TaskEntity> | ElementsValues | TaskEntity[] | EditorType>;

function* saveActiveTaskHandler({payload}: SaveActiveTaskAction): SaveActiveTaskGenerator {
    yield put(updateActiveTaskPrivates({saveInProgress: true}));
    const annotationUrl: string = (yield getAnnotationUrl()) as string;
    const fluxUrl: string = (yield getFluxUrl()) as string;
    const {phase, projectId, jobId}: HistoryParams = getHistoryParams();
    const {id: taskId}: Task = payload;

    const editor: EditorType = (
        yield call(resolveEditorElement, phase)
    ) as EditorType;

    const taskList: TaskEntity[] = (
        yield select((state: StoreState): TaskEntity[] => state.job.tasks)
    ) as TaskEntity[];

    const {id: taskEntityId}: TaskEntity = taskList.find((entity: TaskEntity): boolean => entity.payload === taskId)!;

    const {data: taskEntity}: AxiosResponse<TaskEntity> = (
        yield axios.get(
            `${fluxUrl}/project/${projectId}/job/${jobId}/task/${taskEntityId}`,
        )
    ) as AxiosResponse<TaskEntity>;

    const readOnly: boolean = taskEntity && isReadOnlyTask(taskEntity.phases, phase);

    if (!readOnly) {
        const metadataValid: boolean = (
            yield call(metadataHasErrors, {
                metadataId: taskEntityId,
                elementsValuesType: ElementsValuesType.TASK,
            })
        ) as boolean;

        const schemaKey: string = (
            yield select(({project: {projectDetails}}: StoreState): string => (
                `${projectDetails?.schemaId}_${phase}`
            ))
        ) as string;

        const taskToken: string = (yield getToken({
            projectId, jobId, taskId: taskEntityId, phase,
        })) as string;

        const taskMetadataToSave: ElementsValues = (
            yield call(retrieveElementsValues, {
                metadataId: taskEntityId,
                elementsValuesType: ElementsValuesType.TASK,
            })
        ) as ElementsValues;

        const taskData: TaskData = {};
        Object.keys(taskMetadataToSave).forEach((reference: string): void => {
            if (taskMetadataToSave[reference] !== undefined) {
                taskData[reference] = taskMetadataToSave[reference] as TaskDataType;
            }
        });
        const changedTask: Task = {
            ...payload,
            data: taskData,
        };

        const response: AxiosResponse<TaskSaveResponse> = (
            yield axios.put(
                `${annotationUrl}/task?token=${taskToken}`, changedTask,
            )
        ) as AxiosResponse<TaskSaveResponse>;

        if (response) {
            const taskMetadata: ElementsValues = (yield call(getTaskMetadata, taskEntityId)) as ElementsValues;

            const updatedTaskMetadata: Nullable<ElementsValues> = getUpdatedElementMetadata({
                editor,
                current: taskMetadataToSave,
                next: taskMetadata,
                elementsValuesType: ElementsValuesType.TASK,
            }) as ElementsValues;

            yield put(updateTaskElementsValues({
                schemaKey,
                metadataId: taskEntityId,
                values: updatedTaskMetadata,
            }));

            const fileUploaded: boolean = Boolean(yield select((state: StoreState)
            : Nullable<UploadFile> | Nullable<string> => mergeUploadBuckets(state.uploadFiles.selectedFiles)[taskId]
                || state.job.activeTaskPrivates.signedMediaUrl));

            const valid: boolean = metadataValid && fileUploaded;

            let updatedTaskData: TaskData | undefined;
            if (updatedTaskMetadata) {
                updatedTaskData = {};
                Object.keys(updatedTaskMetadata).forEach((reference: string): void => {
                    if (updatedTaskMetadata[reference] !== undefined) {
                        updatedTaskData![reference] = updatedTaskMetadata[reference] as TaskDataType;
                    }
                });
            }

            // file url data is not known before upload to s3 (complete intake)
            const task: Task = {
                ...payload,
                valid,
                data: updatedTaskData,
            };

            const {data: {version}}: AxiosResponse<TaskSaveResponse> = (
                yield axios.put(
                    `${annotationUrl}/task?token=${taskToken}`, task,
                )
            ) as AxiosResponse<TaskSaveResponse>;

            yield put(updateActiveTask({activeTaskUpdate: {version, valid}}));
            yield put(updateTaskStatus(taskEntity));
            yield put(saveTaskSuccess(task));
        }
    }
    yield put(updateActiveTaskPrivates({saveInProgress: false}));
    yield put(saveCompleted());
}

type StartTaskWizardGenerator = Generator<PutEffect<SetTaskMaskDataAction
| SetFileSelectionTypeAction
| GetActiveTaskDetailsAction>,
void,
boolean | string | AxiosResponse<TaskEntity> | ElementsValues | TaskEntity[]>;

function* startTaskWizard(): StartTaskWizardGenerator {
    yield put(setTaskMaskData({visible: true}));
    yield put(setFileSelectionType(FileSelectionType.SINGLE));
    yield put(getActiveTaskDetails());
}

type ResetTaskDataGenerator = Generator<SelectEffect
| PutEffect<SaveActiveTaskAction
| SetSchemaElementValueAction
| ShowResetTaskDialogAction
| RemoveSelectedFileAction>>;

function* resetTaskDataHandler(): ResetTaskDataGenerator {
    const activeTask: Nullable<Task> = (
        yield select((state: StoreState): Nullable<Task> => state.job.activeTask)
    ) as Nullable<Task>;

    if (activeTask) {
        const {id: taskId}: Task = activeTask;
        const elementsValuesMap: ElementsValuesMap = (
            yield select(
                (state: StoreState): ElementsValuesMap => state.schema.elementsValuesMap,
            )
        ) as ElementsValuesMap;

        const fileUploaded: Nullable<UploadFile> = (
            yield select((state: StoreState): Nullable<UploadFile> => mergeUploadBuckets(
                state.uploadFiles.selectedFiles,
            )[taskId])
        ) as Nullable<UploadFile>;

        const taskList: TaskEntity[] = (
            yield select((state: StoreState): TaskEntity[] => state.job.tasks)
        ) as TaskEntity[];
        const {id: taskEntityId}: TaskEntity = taskList.find((entity: TaskEntity) => entity.payload === taskId)!;

        const {phase}: HistoryParams = getHistoryParams();

        const schemaKey: string = (
            yield select(({project: {projectDetails}}: StoreState): string => (
                `${projectDetails?.schemaId}_${phase}`
            ))
        ) as string;

        const {taskElementsValues}: SchemaElementsValues = elementsValuesMap[schemaKey];
        const elementsValues: ElementsValues = taskElementsValues[taskEntityId];

        if (Object.keys(elementsValues).length) {
            const newTaskValues: ElementsValues = {};
            const {fileUrlReference, fileNameReference, uploadReference}: EditorType = (
                yield select(({schema: {parsedSchemas}}: StoreState): EditorType => (
                    parsedSchemas[`${schemaKey}`] as EditorType)!)
            ) as EditorType;
            if (fileUrlReference) {
                newTaskValues[fileUrlReference] = elementsValues[fileUrlReference];
            }
            if (fileNameReference) {
                newTaskValues[fileNameReference] = elementsValues[fileNameReference];
            }
            if (uploadReference) {
                newTaskValues[uploadReference] = elementsValues[uploadReference];
            }

            const schemaElementsValues: SchemaElementsValues = {
                ...elementsValuesMap[schemaKey],
                taskElementsValues: {
                    ...taskElementsValues,
                    [taskEntityId]: newTaskValues,
                },
            };
            yield put(setSchemaElementValue({
                schemaKey,
                values: schemaElementsValues,
            }));
            yield put(saveActiveTask(activeTask));
        }
        if (fileUploaded) {
            yield put(removeSelectedFile(fileUploaded));
        }
    }

    yield put(showResetTaskDialog(false));
}

type SelectTaskInWizardGenerator = Generator<
SelectEffect
| TakeEffect
| PutEffect<SetTaskMaskDataAction
| SetFileSelectionTypeAction
| GetActiveTaskDetailsAction
| SetActiveTaskByIdAction>,
void,
boolean | string | AxiosResponse<TaskEntity> | ElementsValues | TaskEntity[]>;

function* selectTaskInWizardHandler({payload}: SelectTaskInWizardAction): SelectTaskInWizardGenerator {
    const saveInProgress: boolean = !!(yield select(
        (state: StoreState): boolean => !!state.job.activeTaskPrivates.saveInProgress,
    ));
    yield put(setTaskMaskData({visible: true}));
    yield put(setFileSelectionType(FileSelectionType.SINGLE));

    if (saveInProgress) {
        yield take(SAVE_COMPLETED);
    }
    yield putResolve(setActiveTaskById(payload));
    yield put(getActiveTaskDetails());
}

type HandleTaskInProgressGenerator = Generator<
PutEffect<UpdateTaskEntityByIdAction | GetJobPhaseAction | GetTaskListAction>
| Promise<AxiosResponse<TaskEntity>>
| SelectEffect,
void,
string | JobPhase>;

function* updateTaskStatusHandler({payload}: UpdateTaskStatusAction): HandleTaskInProgressGenerator {
    if (payload) {
        const {phase, projectId, jobId}: HistoryParams = getHistoryParams();
        const fluxUrl: string = (yield getFluxUrl()) as string;
        const {accessStatus, status: phaseStatus}: JobPhase = (
            yield select((state: StoreState): JobPhase => state.job.jobPhase)
        ) as JobPhase;

        if ([Status.READY, Status.REOPENED].includes(payload.status) && !isJobExpired({accessStatus, phaseStatus})) {
            yield axios.patch(`${fluxUrl}/project/${projectId}/job/${jobId}/task/${payload.id}/phase/${phase}`,
                {
                    status: Status.IN_PROGRESS,
                });
            yield put(getJobPhase());
            yield put(updateTaskEntityById({
                ...payload,
                status: Status.IN_PROGRESS,
            }));
        }
    }
}

export function* actionWatcher(): IterableIterator<AllEffect<ForkEffect>> {
    yield all([
        takeLatest(GET_TASK_LIST, safeSaga(fetchTaskList)),
        takeLatest(GET_ACTIVE_TASK_DETAILS, safeSaga(fetchActiveTaskData)),
        takeLatest(CHANGE_ACTIVE_TASK, safeSaga(changeActiveTaskHandler)),
        takeLatest(SAVE_ACTIVE_TASK, safeSaga(saveActiveTaskHandler)),
        takeLatest(START_TASK_WIZARD, safeSaga(startTaskWizard)),
        takeLatest(RESET_TASK_DATA, safeSaga(resetTaskDataHandler)),
        takeLatest(SELECT_TASK_IN_WIZARD, safeSaga(selectTaskInWizardHandler)),
        takeLatest(UPDATE_TASK_STATUS, safeSaga(updateTaskStatusHandler)),
    ]);
}
